import React, { useState, useEffect } from 'react';

interface Testimonial {
  content: string;
  author: string;
  position: string;
  company: string;
  image?: string;
}

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ testimonials }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-scroll testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 6000);
    
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
            Client Success Stories
          </span>
          <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
          Trusted by Industry Leaders
          </h2>
          <p className="md:text-lg text-gray-600 px-4">
          See how we've helped businesses transform their ideas into successful, scalable solutions
          </p>
        </div>

        <div className="max-w-4xl mx-auto px-4">
          <div className="relative bg-white rounded-xl shadow-lg p-6 md:p-10">
            <div className="absolute -top-6 -left-6">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.976 30C12.432 30 10.368 29.136 8.784 27.408C7.248 25.632 6.48 23.328 6.48 20.496C6.48 17.856 7.152 15.408 8.496 13.152C9.84 10.896 11.664 9.024 13.968 7.536L16.416 10.56C14.592 11.808 13.2 13.248 12.24 14.88C11.28 16.512 10.8 18.24 10.8 20.064C10.944 19.968 11.328 19.92 11.952 19.92C13.584 19.92 14.928 20.496 15.984 21.648C17.04 22.752 17.568 24.24 17.568 26.112C17.568 27.696 17.04 29.04 15.984 30.144C14.976 30.048 14.976 30 14.976 30ZM31.344 30C28.8 30 26.736 29.136 25.152 27.408C23.616 25.632 22.848 23.328 22.848 20.496C22.848 17.856 23.52 15.408 24.864 13.152C26.208 10.896 28.032 9.024 30.336 7.536L32.784 10.56C30.96 11.808 29.568 13.248 28.608 14.88C27.648 16.512 27.168 18.24 27.168 20.064C27.312 19.968 27.696 19.92 28.32 19.92C29.952 19.92 31.296 20.496 32.352 21.648C33.408 22.752 33.936 24.24 33.936 26.112C33.936 27.696 33.408 29.04 32.352 30.144C31.344 30.048 31.344 30 31.344 30Z" fill="#4F46E5" fillOpacity="0.2"/>
              </svg>
            </div>
            
            <div className="relative overflow-hidden" style={{ minHeight: '270px' }}>
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  <blockquote className="mb-8">
                    <p className="text-lg md:text-2xl text-gray-700 italic leading-relaxed mb-6">
                      "{testimonial.content}"
                    </p>
                    <footer className="flex items-center">
                      {testimonial.image ? (
                        <div className="mr-4">
                          <div className="w-12 h-12 rounded-full overflow-hidden">
                            <img 
                              src={testimonial.image} 
                              alt={testimonial.author} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="mr-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                            {testimonial.author.charAt(0)}
                          </div>
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-gray-900">{testimonial.author}</div>
                        <div className="text-sm text-gray-600">{testimonial.position}, {testimonial.company}</div>
                      </div>
                    </footer>
                  </blockquote>
                </div>
              ))}
            </div>
            
            <div className="flex justify-center mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 rounded-full mx-1 transition-colors duration-300 ${
                    index === activeIndex ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;